import { plansLib } from "@agentcall/core/lib"
import { formatPhone } from "@agentcall/core/lib/tenant_lib"
import { activePlanMasters } from "@agentcall/core/master"
import { planMasterType } from "@agentcall/core/type"
import { zodResolver } from "@hookform/resolvers/zod"
import { X } from "lucide-react"
import { useCallback, useEffect, useState } from "react"
import { useForm } from "react-hook-form"
import { z } from "zod"
import { roomList } from "@/api/generated/endpoints/api"
import type { RoomListParams } from "@/api/generated/models/roomListParams"
import { Button } from "@/components/ui/button"
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog"
import { Input } from "@/components/ui/input"
import {
  Pagination,
  PaginationContent,
  PaginationFirst,
  PaginationItem,
  PaginationLast,
  PaginationLink,
  PaginationNext,
  PaginationPrevious,
} from "@/components/ui/pagination"
import { PlanLabel, type PlanMasterIdOrNull } from "@/components/ui/plan-label"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"

// 検索フォームのスキーマ
const searchFormSchema = z.object({
  propertyName: z.string().optional(),
  roomNumber: z.string().optional(),
  propertyAddress: z.string().optional(),
  tenantName: z.string().optional(),
  ownerName: z.string().optional(),
})

type SearchFormValues = z.infer<typeof searchFormSchema>

// 検索結果の型
export interface ContractorSearchResult {
  tenantId: number
  ownerId: string
  ownerName: string
  propertyId: number
  propertyName: string
  roomId: number
  roomNumber: string
  address: string
  tenantName: string
  tenantPhone: string
  planMasterId: PlanMasterIdOrNull
  contractStartDate: string
  contractEndDate: string | null
  propertyNote: string
  tenantAdminMemo: string
  ownerAdminMemo: string | null
}

// 選択結果の型
export interface SelectedContractor {
  tenantId?: number
  ownerId: string
  propertyId: number
  roomId: number | null
  tenant: string
  property: string
  plan: string
  room: string
  phone: string
  owner: string
  startDate: string
  propertyNote?: string
  tenantAdminMemo?: string
  ownerAdminMemo?: string
}

interface ContractorSearchModalProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  onSelect: (contractor: SelectedContractor) => void
  onSelectByProperty?: (contractor: SelectedContractor) => void
}

// プランのラベル名取得
const getPlanName = (planMasterId: PlanMasterIdOrNull): string => {
  if (!planMasterId) return "-"
  const planMaster = activePlanMasters.find((pm) => pm.id === planMasterId)
  return planMaster?.name ?? "-"
}

const ITEMS_PER_PAGE = 10

export function ContractorSearchModal({
  open,
  onOpenChange,
  onSelect,
  onSelectByProperty,
}: ContractorSearchModalProps) {
  const [searchResults, setSearchResults] = useState<ContractorSearchResult[]>([])
  const [isSearched, setIsSearched] = useState(false)
  const [isLoading, setIsLoading] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [searchParams, setSearchParams] = useState<SearchFormValues>({})

  const totalPages = Math.ceil(totalCount / ITEMS_PER_PAGE)

  const form = useForm<SearchFormValues>({
    resolver: zodResolver(searchFormSchema),
    defaultValues: {
      propertyName: "",
      roomNumber: "",
      propertyAddress: "",
      tenantName: "",
      ownerName: "",
    },
  })

  const fetchRooms = useCallback(async (params: SearchFormValues, page: number) => {
    setIsLoading(true)
    try {
      const apiParams: RoomListParams = {
        page,
        limit: ITEMS_PER_PAGE,
        withProperty: "true",
        withPropertyOwner: "true",
        withTenants: "true",
        withPlans: "true",
      }

      // 検索パラメータを追加（空文字列でない場合のみ）
      if (params.propertyName) {
        apiParams.propertyNameLike = params.propertyName
      }
      if (params.roomNumber) {
        apiParams.nameLike = params.roomNumber
      }
      if (params.propertyAddress) {
        apiParams.propertyAddressLike = params.propertyAddress
      }
      if (params.tenantName) {
        apiParams.tenantNameLike = params.tenantName
      }
      if (params.ownerName) {
        apiParams.ownerNameLike = params.ownerName
      }

      const response = await roomList(apiParams)

      // APIレスポンスをContractorSearchResultに変換（部屋ごとに賃借人を展開）
      const results: ContractorSearchResult[] = []
      for (const room of response.rooms) {
        const property = room.property
        const owner = property?.owner

        // プラン情報を取得（アクティブなプランを探す）
        let planMasterId: PlanMasterIdOrNull = null
        const plans = room.plans
        if (plans && plans.length > 0) {
          const currentPlans = plansLib.getCurrentPlans(plans, null, null)
          const activePlan = currentPlans[0]
          if (
            activePlan?.planMasterId === planMasterType.PLAN_MASTER_ID_STANDARD ||
            activePlan?.planMasterId === planMasterType.PLAN_MASTER_ID_PLUS
          ) {
            planMasterId = activePlan.planMasterId
          }
        }

        // 住所を組み立て
        const address = property
          ? `${property.prefecture}${property.city}${property.town}${property.district}`
          : ""

        // 賃借人がいる場合は各賃借人ごとに1行
        const tenants = room.tenants ?? []
        if (tenants.length > 0) {
          for (const tenant of tenants) {
            results.push({
              tenantId: tenant.id,
              // property.ownerId は常に存在するので空文字にならない
              ownerId: property?.ownerId ?? "",
              ownerName: owner?.name ?? "",
              propertyId: property?.id ?? 0,
              propertyName: property?.name ?? "",
              roomId: room.id,
              roomNumber: room.name,
              address,
              tenantName: tenant.name,
              tenantPhone: formatPhone(tenant.phone1, tenant.phone2, tenant.phone3),
              planMasterId,
              contractStartDate: tenant.startDate,
              contractEndDate: tenant.endDate,
              propertyNote: property?.note ?? "",
              tenantAdminMemo: tenant.adminMemo ?? "",
              ownerAdminMemo: owner?.adminMemo ?? null,
            })
          }
        } else {
          // 賃借人がいない部屋も表示
          results.push({
            tenantId: -1,
            ownerId: property?.ownerId ?? "",
            ownerName: owner?.name ?? "",
            propertyId: property?.id ?? 0,
            propertyName: property?.name ?? "",
            roomId: room.id,
            roomNumber: room.name,
            address,
            tenantName: "-",
            tenantPhone: "-",
            planMasterId,
            contractStartDate: "-",
            contractEndDate: null,
            propertyNote: property?.note ?? "",
            tenantAdminMemo: "",
            ownerAdminMemo: owner?.adminMemo ?? null,
          })
        }
      }

      // 展開後の結果を10件に制限して表示
      setSearchResults(results.slice(0, ITEMS_PER_PAGE))
      setTotalCount(response.total)
      setIsSearched(true)
    } catch (error) {
      console.error("検索エラー:", error)
      setSearchResults([])
      setTotalCount(0)
    } finally {
      setIsLoading(false)
    }
  }, [])

  // モーダルが開いた時に初回データを取得
  useEffect(() => {
    if (open && !isSearched) {
      fetchRooms({}, 1)
    }
    // モーダルが閉じた時に状態をリセット
    if (!open) {
      setIsSearched(false)
      setSearchResults([])
      setCurrentPage(1)
      setTotalCount(0)
      setSearchParams({})
      form.reset()
    }
  }, [open, isSearched, fetchRooms, form])

  const handleSearch = (values: SearchFormValues) => {
    setSearchParams(values)
    setCurrentPage(1)
    fetchRooms(values, 1)
  }

  const handlePageChange = (page: number) => {
    setCurrentPage(page)
    fetchRooms(searchParams, page)
  }

  const handleReset = () => {
    form.reset()
    setSearchResults([])
    setIsSearched(false)
    setCurrentPage(1)
    setTotalCount(0)
    setSearchParams({})
  }

  const handleSelectContractor = (result: ContractorSearchResult) => {
    const selectedData: SelectedContractor = {
      tenantId: result.tenantId,
      ownerId: result.ownerId,
      propertyId: result.propertyId,
      roomId: result.roomId,
      tenant: result.tenantName,
      property: result.propertyName,
      plan: getPlanName(result.planMasterId),
      room: `${result.roomNumber}号室`,
      phone: result.tenantPhone,
      owner: result.ownerName,
      startDate: result.contractStartDate,
      propertyNote: result.propertyNote,
      tenantAdminMemo: result.tenantAdminMemo,
      ownerAdminMemo: result.ownerAdminMemo ?? undefined,
    }
    onSelect(selectedData)
    onOpenChange(false)
  }

  const handleSelectByProperty = (result: ContractorSearchResult) => {
    const selectedData: SelectedContractor = {
      ownerId: result.ownerId,
      propertyId: result.propertyId,
      roomId: null,
      tenant: "",
      property: result.propertyName,
      plan: "",
      room: "",
      phone: "",
      owner: result.ownerName,
      startDate: "",
      propertyNote: result.propertyNote,
      ownerAdminMemo: result.ownerAdminMemo ?? undefined,
    }
    if (onSelectByProperty) {
      onSelectByProperty(selectedData)
    } else {
      onSelect(selectedData)
    }
    onOpenChange(false)
  }

  // ページ番号の配列を生成
  const getPageNumbers = () => {
    const pages: number[] = []
    const maxVisiblePages = 5
    let startPage = Math.max(1, currentPage - Math.floor(maxVisiblePages / 2))
    const endPage = Math.min(totalPages, startPage + maxVisiblePages - 1)

    if (endPage - startPage + 1 < maxVisiblePages) {
      startPage = Math.max(1, endPage - maxVisiblePages + 1)
    }

    for (let i = startPage; i <= endPage; i++) {
      pages.push(i)
    }
    return pages
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[1148px] max-h-[90vh] overflow-y-auto p-0">
        <div className="p-[25px]">
          <DialogHeader className="mb-5">
            <DialogTitle className="text-[18px] font-semibold text-[#3f4652]">
              契約者情報検索
            </DialogTitle>
          </DialogHeader>

          <button
            type="button"
            onClick={() => onOpenChange(false)}
            className="absolute right-[14px] top-[14px] rounded-sm opacity-70 ring-offset-background transition-opacity hover:opacity-100"
          >
            <X className="h-[18px] w-[18px] text-[#354267]" />
            <span className="sr-only">閉じる</span>
          </button>

          <div className="space-y-[30px]">
            {/* 検索フォーム */}
            <div className="border border-slate-200 rounded-[10px]">
              <form
                onSubmit={form.handleSubmit(handleSearch)}
                className="py-[25px] w-[434px] mx-auto"
              >
                <div className="space-y-[15px]">
                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-[#3f4652] w-[84px]">物件名</label>
                    <div className="w-[350px]">
                      <Input {...form.register("propertyName")} />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-[#3f4652] w-[84px]">号室</label>
                    <div className="w-[350px]">
                      <Input {...form.register("roomNumber")} />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-[#3f4652] w-[84px]">
                      物件住所
                    </label>
                    <div className="w-[350px]">
                      <Input {...form.register("propertyAddress")} />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-[#3f4652] w-[84px]">
                      賃借人名
                    </label>
                    <div className="w-[350px]">
                      <Input {...form.register("tenantName")} />
                    </div>
                  </div>

                  <div className="flex items-center justify-between">
                    <label className="text-xs font-semibold text-[#3f4652] w-[84px]">
                      オーナー名
                    </label>
                    <div className="w-[350px]">
                      <Input {...form.register("ownerName")} />
                    </div>
                  </div>
                </div>

                <div className="flex justify-center gap-5 mt-5">
                  <Button type="submit" variant="default" disabled={isLoading}>
                    {isLoading ? "検索中..." : "検索する"}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={handleReset}
                    disabled={isLoading}
                  >
                    検索をリセット
                  </Button>
                </div>
              </form>
            </div>

            {/* 検索結果テーブル */}
            {isSearched && (
              <div className="space-y-4">
                <div className="rounded-[10px] overflow-hidden">
                  <Table>
                    <TableHeader>
                      <TableRow className="bg-white border border-slate-200">
                        <TableHead className="whitespace-nowrap text-xs font-semibold text-[#3f4652] h-8 px-4">
                          オーナー名
                        </TableHead>
                        <TableHead className="whitespace-nowrap text-xs font-semibold text-[#3f4652] h-8 px-4">
                          物件名
                        </TableHead>
                        <TableHead className="whitespace-nowrap text-xs font-semibold text-[#3f4652] h-8 px-4">
                          号室
                        </TableHead>
                        <TableHead className="whitespace-nowrap text-xs font-semibold text-[#3f4652] h-8 px-4">
                          住所
                        </TableHead>
                        <TableHead className="whitespace-nowrap text-xs font-semibold text-[#3f4652] h-8 px-4">
                          賃借人名
                        </TableHead>
                        <TableHead className="whitespace-nowrap text-xs font-semibold text-[#3f4652] h-8 px-4">
                          プラン加入状況
                        </TableHead>
                        <TableHead className="whitespace-nowrap text-xs font-semibold text-[#3f4652] h-8 px-4">
                          契約開始日
                        </TableHead>
                        <TableHead className="whitespace-nowrap text-xs font-semibold text-[#3f4652] h-8 px-4">
                          契約終了日
                        </TableHead>
                        <TableHead className="whitespace-nowrap text-xs font-semibold text-[#3f4652] h-8 px-4">
                          案件へ適用
                        </TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {isLoading ? (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center py-8 text-slate-400">
                            読み込み中...
                          </TableCell>
                        </TableRow>
                      ) : searchResults.length === 0 ? (
                        <TableRow>
                          <TableCell colSpan={9} className="text-center py-8 text-slate-400">
                            検索結果がありません
                          </TableCell>
                        </TableRow>
                      ) : (
                        searchResults.map((result) => (
                          <TableRow
                            key={
                              result.tenantId > 0
                                ? `tenant-${result.tenantId}`
                                : `room-${result.roomId}`
                            }
                            className="border border-slate-200"
                          >
                            <TableCell className="text-xs font-medium text-[#3f4652] py-2 px-4">
                              {result.ownerName}
                            </TableCell>
                            <TableCell className="text-xs font-medium text-[#3f4652] py-2 px-4">
                              {result.propertyName}
                            </TableCell>
                            <TableCell className="text-xs font-medium text-[#3f4652] py-2 px-4">
                              {result.roomNumber}
                            </TableCell>
                            <TableCell className="text-xs font-medium text-[#3f4652] py-2 px-4">
                              {result.address}
                            </TableCell>
                            <TableCell className="text-xs font-medium text-[#3f4652] py-2 px-4">
                              {result.tenantName}
                            </TableCell>
                            <TableCell className="py-2 px-4">
                              <PlanLabel planMasterId={result.planMasterId} />
                            </TableCell>
                            <TableCell className="text-xs font-medium text-[#3f4652] py-2 px-4">
                              {result.contractStartDate}
                            </TableCell>
                            <TableCell className="text-xs font-medium text-[#3f4652] py-2 px-4">
                              {result.contractEndDate || "-"}
                            </TableCell>
                            <TableCell className="py-2 px-4">
                              <div className="flex gap-2">
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleSelectContractor(result)}
                                  className="text-xs font-bold"
                                >
                                  この内容で適用
                                </Button>
                                <Button
                                  type="button"
                                  variant="outline"
                                  size="sm"
                                  onClick={() => handleSelectByProperty(result)}
                                  className="text-xs font-bold"
                                >
                                  物件で適用
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                </div>

                {/* ページネーション */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between">
                    <p className="text-xs text-slate-500">
                      全{totalCount}部屋から {searchResults.length}件の賃借人を表示中
                    </p>
                    <Pagination>
                      <PaginationContent>
                        <PaginationItem>
                          <PaginationFirst
                            onClick={() => handlePageChange(1)}
                            disabled={currentPage === 1 || isLoading}
                          />
                        </PaginationItem>
                        <PaginationItem>
                          <PaginationPrevious
                            onClick={() => handlePageChange(currentPage - 1)}
                            disabled={currentPage === 1 || isLoading}
                          />
                        </PaginationItem>
                        {getPageNumbers().map((page) => (
                          <PaginationItem key={page}>
                            <PaginationLink
                              onClick={() => handlePageChange(page)}
                              isActive={page === currentPage}
                              className="h-8 w-8 cursor-pointer"
                            >
                              {page}
                            </PaginationLink>
                          </PaginationItem>
                        ))}
                        <PaginationItem>
                          <PaginationNext
                            onClick={() => handlePageChange(currentPage + 1)}
                            disabled={currentPage === totalPages || isLoading}
                          />
                        </PaginationItem>
                        <PaginationItem>
                          <PaginationLast
                            onClick={() => handlePageChange(totalPages)}
                            disabled={currentPage === totalPages || isLoading}
                          />
                        </PaginationItem>
                      </PaginationContent>
                    </Pagination>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  )
}
